import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessPage, type PageKey } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";
import {
  Search,
  LayoutDashboard,
  Building2,
  FileText,
  ClipboardList,
  FolderKanban,
  Users,
  DollarSign,
  MessageSquare,
  Headset,
  Home,
  TrendingUp,
  BarChart2,
  CalendarDays,
  Rocket,
  UserCheck,
  Gift,
  AlertTriangle,
  Hammer,
  PhoneIncoming,
  Scale,
  Receipt,
  Timer,
  CreditCard,
  Bell,
  BarChart3,
  MessageSquareWarning,
  Shield,
  RotateCcw,
  Filter,
  Wrench,
  Briefcase,
  ArrowRight,
  Radio,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface PageEntry {
  path: string;
  label: string;
  icon: React.ElementType;
  pageKey?: PageKey;
  keywords?: string;
}

const ALL_PAGES: PageEntry[] = [
  { path: "/", label: "Dashboard principal", icon: LayoutDashboard, pageKey: "DASHBOARD", keywords: "accueil home" },
  { path: "/companies", label: "Toutes les entreprises", icon: Building2, pageKey: "COMPANIES", keywords: "societe client" },
  { path: "/conversion-pipeline", label: "Pipeline conversion", icon: TrendingUp, pageKey: "CONVERSION_PIPELINE" },
  { path: "/onboarding", label: "Onboarding", icon: Rocket, pageKey: "ONBOARDING" },
  { path: "/churn-risk", label: "Churn Risk", icon: AlertTriangle, pageKey: "CHURN_RISK", keywords: "risque attrition" },
  { path: "/private-projects", label: "Projets particuliers", icon: Home, pageKey: "PRIVATE_PROJECTS", keywords: "travaux demande" },
  { path: "/travauxbtp/artisans", label: "Moderation artisans", icon: UserCheck, pageKey: "ARTISANS_MODERATION", keywords: "verification pro" },
  { path: "/travauxbtp/artisans/all", label: "Annuaire artisans", icon: Users, pageKey: "ARTISANS_MODERATION", keywords: "liste tous annuaire fiche" },
  { path: "/travauxbtp/reviews", label: "Moderation avis", icon: Shield, pageKey: "REVIEWS_MODERATION" },
  { path: "/messagerie-moderation", label: "Moderation messages", icon: MessageSquareWarning, pageKey: "MESSAGE_MODERATION" },
  { path: "/sla-leads", label: "SLA Leads", icon: Timer, pageKey: "SLA_LEADS" },
  { path: "/disputes", label: "Litiges", icon: Scale, pageKey: "DISPUTES" },
  { path: "/travauxbtp", label: "Dashboard TravauxBTP", icon: Hammer, pageKey: "TRAVAUXBTP_DASHBOARD" },
  { path: "/travauxbtp/referrals", label: "Parrainages", icon: Gift, pageKey: "TRAVAUXBTP_REFERRALS" },
  { path: "/lp-leads", label: "LP Leads", icon: PhoneIncoming, pageKey: "LP_LEADS" },
  { path: "/transactions", label: "Transactions credits", icon: Receipt, pageKey: "TRANSACTIONS" },
  { path: "/refunds", label: "Remboursements", icon: RotateCcw, pageKey: "REFUNDS" },
  { path: "/marketplace-stats", label: "Stats Marketplace", icon: BarChart3, pageKey: "MARKETPLACE_STATS" },
  { path: "/acquisition-funnel", label: "Funnel acquisition", icon: Filter, pageKey: "ACQUISITION_FUNNEL", keywords: "funnel signup signups acquisition sante" },
  { path: "/tenders", label: "Appels d'offres", icon: FileText, pageKey: "TENDERS", keywords: "AO marche" },
  { path: "/go-queue", label: "GO Queue", icon: ClipboardList, pageKey: "GO_QUEUE", keywords: "validation qualification" },
  { path: "/sales/pipeline", label: "Pipeline Commercial", icon: TrendingUp, pageKey: "SALES_PIPELINE" },
  { path: "/sales/leads", label: "Leads", icon: Users, pageKey: "SALES_LEADS", keywords: "prospects contacts" },
  { path: "/sales/activities", label: "Activites", icon: CalendarDays, pageKey: "SALES_ACTIVITIES" },
  { path: "/sales/performance", label: "Performance", icon: BarChart2, pageKey: "SALES_PERFORMANCE" },
  { path: "/sales/forecast", label: "Previsions", icon: BarChart3, pageKey: "SALES_FORECAST" },
  { path: "/subscriptions", label: "Abonnements", icon: CreditCard, pageKey: "SUBSCRIPTIONS" },
  { path: "/trial-alerts", label: "Alertes Trials", icon: Bell, pageKey: "TRIAL_ALERTS", keywords: "essai expiration" },
  { path: "/partners", label: "Partenaires", icon: Users, pageKey: "PARTNERS" },
  { path: "/projects", label: "Projets", icon: FolderKanban, pageKey: "PROJECTS" },
  { path: "/messages", label: "Messagerie", icon: MessageSquare, pageKey: "MESSAGES" },
  { path: "/support", label: "Support", icon: Headset, pageKey: "SUPPORT" },
  { path: "/billing", label: "Facturation", icon: DollarSign, pageKey: "BILLING" },
  { path: "/broadcast", label: "Broadcast", icon: Radio, pageKey: "BROADCAST", keywords: "envoi message notification" },
  { path: "/audit-log", label: "Journal d'audit", icon: ClipboardList, pageKey: "AUDIT_LOG" },
  { path: "/search", label: "Recherche globale", icon: Search },
];

interface QuickAction {
  label: string;
  path: string;
  keywords: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  { label: "Voir les alertes trials urgentes", path: "/trial-alerts", keywords: "urgent trial expiration" },
  { label: "Litiges ouverts", path: "/disputes", keywords: "dispute conflit" },
  { label: "Artisans en attente de verification", path: "/travauxbtp/artisans", keywords: "pending verification" },
  { label: "SLA leads critiques", path: "/sla-leads", keywords: "sla urgent remboursement" },
];

interface ResultItem {
  id: string;
  type: "page" | "action" | "user" | "company" | "artisan" | "project";
  label: string;
  sublabel?: string;
  path: string;
  icon: React.ElementType;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const trimmed = query.trim().toLowerCase();
  const searchEnabled = trimmed.length >= 2;

  const { data: searchData } = trpc.admin.search.global.useQuery(
    { query: trimmed, limit: 5 },
    { enabled: searchEnabled && open, staleTime: 30_000 }
  );

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const filteredPages = useMemo(() => {
    const role = user?.staffRole;
    return ALL_PAGES.filter((p) => {
      if (p.pageKey && !canAccessPage(role, p.pageKey)) return false;
      if (!trimmed) return true;
      const haystack = `${p.label} ${p.keywords || ""}`.toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [trimmed, user?.staffRole]);

  const filteredActions = useMemo(() => {
    if (!trimmed) return QUICK_ACTIONS;
    return QUICK_ACTIONS.filter((a) => {
      const haystack = `${a.label} ${a.keywords}`.toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [trimmed]);

  const results = useMemo<ResultItem[]>(() => {
    const items: ResultItem[] = [];

    for (const p of filteredPages.slice(0, 8)) {
      items.push({
        id: `page-${p.path}`,
        type: "page",
        label: p.label,
        path: p.path,
        icon: p.icon,
      });
    }

    for (const a of filteredActions.slice(0, 4)) {
      items.push({
        id: `action-${a.path}-${a.label}`,
        type: "action",
        label: a.label,
        path: a.path,
        icon: ArrowRight,
      });
    }

    if (searchData) {
      for (const u of (searchData.users ?? []).slice(0, 3)) {
        items.push({
          id: `user-${u.id}`,
          type: "user",
          label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
          sublabel: u.email,
          path: u.companyId ? `/companies/${u.companyId}` : "/companies",
          icon: Users,
        });
      }
      for (const c of (searchData.companies ?? []).slice(0, 3)) {
        items.push({
          id: `company-${c.id}`,
          type: "company",
          label: c.name,
          sublabel: c.siret ? `SIRET ${c.siret}` : c.email ?? undefined,
          path: `/companies/${c.id}`,
          icon: Building2,
        });
      }
      for (const a of (searchData.artisans ?? []).slice(0, 3)) {
        items.push({
          id: `artisan-${a.id}`,
          type: "artisan",
          label: a.companyName,
          sublabel: a.serviceCity ?? undefined,
          path: a.User?.companyId ? `/companies/${a.User.companyId}` : "/travauxbtp/artisans",
          icon: Wrench,
        });
      }
      for (const p of (searchData.projects ?? []).slice(0, 3)) {
        items.push({
          id: `project-${p.id}`,
          type: "project",
          label: p.title,
          sublabel: `${p.category} · ${p.city ?? ""}`,
          path: `/particuliers/${p.id}`,
          icon: Briefcase,
        });
      }
    }

    return items;
  }, [filteredPages, filteredActions, searchData]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const select = useCallback(
    (item: ResultItem) => {
      setOpen(false);
      navigate(item.path);
    },
    [navigate]
  );

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[activeIndex]) {
      e.preventDefault();
      select(results[activeIndex]);
    }
  }

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const typeLabel = (type: ResultItem["type"]) => {
    switch (type) {
      case "page": return null;
      case "action": return null;
      case "user": return "Utilisateur";
      case "company": return "Entreprise";
      case "artisan": return "Artisan";
      case "project": return "Projet";
    }
  };

  let currentSection = "";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Palette de commandes</DialogTitle>
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher une page, un contact, une entreprise..."
            className="flex-1 h-11 bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden sm:inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
            esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[320px] overflow-y-auto py-1">
          {results.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Aucun resultat
            </div>
          )}
          {results.map((item, i) => {
            const Icon = item.icon;
            let sectionHeader: string | null = null;
            const section =
              item.type === "page" ? "Pages" :
              item.type === "action" ? "Actions rapides" :
              "Resultats";
            if (section !== currentSection) {
              currentSection = section;
              sectionHeader = section;
            }
            return (
              <div key={item.id}>
                {sectionHeader && (
                  <div className="px-3 pt-2 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {sectionHeader}
                  </div>
                )}
                <button
                  type="button"
                  data-index={i}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors",
                    i === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
                  )}
                  onClick={() => select(item)}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{item.label}</div>
                    {item.sublabel && (
                      <div className="text-xs text-muted-foreground truncate">{item.sublabel}</div>
                    )}
                  </div>
                  {typeLabel(item.type) && (
                    <span className="text-xs text-muted-foreground shrink-0">{typeLabel(item.type)}</span>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t px-3 py-1.5 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-2">
            <span><kbd className="rounded border px-1 py-0.5">↑↓</kbd> naviguer</span>
            <span><kbd className="rounded border px-1 py-0.5">↵</kbd> ouvrir</span>
          </div>
          <span><kbd className="rounded border px-1 py-0.5">esc</kbd> fermer</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
